import { getToken } from "@/features/auth/authStore";
import { sanitizeApiMessage } from "./sanitize";

declare global {
  var __API_URL__: string | undefined;
}

function getBaseUrl(): string {
  return globalThis.__API_URL__ ?? "http://localhost/api/v1";
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function apiFetch<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options?.headers as Record<string, string>),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    let message = res.statusText;
    let code: string | undefined;
    try {
      const body = await res.json();
      message = body.message ?? body.error ?? message;
      code = body.code;
    } catch {
      // ignore parse error
    }
    if (res.status === 403 && code === "account_deactivated") {
      const { router } = await import("@/router");
      router.navigate({ to: "/conta-desativada" });
    }
    throw new ApiError(res.status, sanitizeApiMessage(message), code);
  }

  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}

export async function apiFetchText(
  path: string,
  options?: RequestInit,
): Promise<string> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string>),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${getBaseUrl()}${path}`, { ...options, headers });

  if (!res.ok) {
    let message = res.statusText;
    let code: string | undefined;
    try {
      const body = await res.json();
      message = body.message ?? body.error ?? message;
      code = body.code;
    } catch {
      // ignore
    }
    throw new ApiError(res.status, sanitizeApiMessage(message), code);
  }
  return res.text();
}

export async function apiFetchBlob(
  path: string,
  options?: RequestInit,
): Promise<{ blob: Blob; filename?: string; headers: Headers }> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string>),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${getBaseUrl()}${path}`, { ...options, headers });

  if (!res.ok) {
    let message = res.statusText;
    let code: string | undefined;
    try {
      const body = await res.json();
      message = body.message ?? body.error ?? message;
      code = body.code;
    } catch {
      // ignore
    }
    throw new ApiError(res.status, sanitizeApiMessage(message), code);
  }

  return {
    blob: await res.blob(),
    headers: res.headers,
    filename: parseContentDispositionFilename(
      res.headers.get("content-disposition"),
    ),
  };
}

function parseContentDispositionFilename(value: string | null) {
  if (!value) return undefined;
  const quoted = value.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) return quoted[1].trim();
  const unquoted = value.match(/filename=([^;]+)/i);
  return unquoted?.[1]?.trim();
}

export function getErrMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Erro desconhecido";
}
